<template>
  <div class="w-full h-full">
    <AlertDialog :open="showErrorModal" @update:open="(o) => !o && (showErrorModal = false)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Internet connection error</AlertDialogTitle>
          <AlertDialogDescription>It appears you're not connected to the internet. Please check your connection and try again.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction @click="showErrorModal = false">OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <AlertDialog :open="confirmModal.open" @update:open="(v) => !v && (confirmModal.open = false)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ confirmModal.title }}</AlertDialogTitle>
          <AlertDialogDescription>{{ confirmModal.description }}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="confirmModal.open = false">Cancel</AlertDialogCancel>
          <AlertDialogAction @click="confirmProceed()">Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <AlertDialog :open="showSuccessModal" @update:open="(o) => { if (!o) { showSuccessModal = false; getKeys() } }">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Copy &amp; store your API key</AlertDialogTitle>
          <AlertDialogDescription>
            This key will not be shown again, copy it now and store it somewhere secure.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <!-- The key in a readable, selectable box so it can be copied by hand too. -->
        <div class="rounded-lg border border-border bg-muted/50 px-3 py-2.5">
          <code class="block select-all break-all font-mono text-sm text-foreground">{{ newKey }}</code>
        </div>
        <!-- Big, primary copy action — the main thing to do here. -->
        <Button class="w-full" @click="copyApiKey">
          <component :is="keyCopied ? 'Check' : 'Copy'" class="mr-2 h-4 w-4" />
          {{ keyCopied ? 'Copied to clipboard' : 'Copy API key' }}
        </Button>
        <AlertDialogFooter>
          <AlertDialogCancel @click="() => { showSuccessModal = false; getKeys() }">Done</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Drawer direction="right" :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Create key</DrawerTitle>
          <DrawerDescription>Create an API key</DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="create-api-key-request-api-key-name">API Key name</label>
            <Input id="create-api-key-request-api-key-name" v-model="createAPIKeyRequest.apiKeyName" type="text" />
          </div>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showFormModal = false">Cancel</Button>
          <Button :disabled="createAPIKeyRequest.apiKeyName == ''" @click="createAPIKey()">Create Key</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
    <Card>
      <CardHeader>
        <CardTitle>API keys</CardTitle>
        <CardDescription>Manage your API keys</CardDescription>
        <CardAction>
          <Button id="createContract" size="sm" @click="showFormModal = true">
            <Plus class="w-4 h-4" />
            Create API key
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="apiKeys"
          :loading="loading"
          :error="error"
          @retry="getKeys()"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
        >
          <template #item-createdOn="apiKeys">
            <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(apiKeys.createdOn) }}</span>
          </template>
          <template #item-active="apiKeys">
            <Badge v-if="apiKeys.active" variant="success">Active</Badge>
            <Badge v-else variant="warning">Deactivated</Badge>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button size="sm" v-if="!retailers.active" @click="askConfirm('Activate API key', 'This key will be able to authenticate requests again.', () => activateKey(retailers.id))">
                <Check class="w-4 h-4" />
                Activate
              </Button>
              <Button size="sm" variant="destructive" v-else @click="askConfirm('Deactivate API key', 'Requests using this key will stop working until it is reactivated.', () => deactivate(retailers.id))">
                <X class="w-4 h-4" />
                Deactivate
              </Button>
              <Button size="sm" variant="destructive" @click="askConfirm('Delete API key', 'This permanently deletes the key. This cannot be undone.', () => deleteKey(retailers.id))">
                <Trash2 class="w-4 h-4" />
              </Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Button, Badge, Input,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
  toast,
} from '@boogi/ui'
import { Plus, Check, X, Trash2, Copy } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Input,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    Plus,
    Check,
    X,
    Trash2,
    Copy,
  },
  props: {},

  data() {
    return {
      keyCopied: false,
      newKey: undefined,
      confirmModal: { open: false, title: '', description: '', action: null },

      createAPIKeyRequest: {
        apiKeyName: '',
      },
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Name', value: 'apiName' },
        { text: 'Created on', value: 'createdOn' },
        { text: 'Status', value: 'active' },
        { text: 'Action', value: 'action' },
      ],
      apiKeys: [],
    }
  },
  created() {
    this.getKeys()
  },
  methods: {
    // Open the shared confirmation modal; `action` runs on confirm.
    askConfirm(title, description, action) {
      this.confirmModal = { open: true, title, description, action }
    },
    confirmProceed() {
      const action = this.confirmModal.action
      this.confirmModal.open = false
      if (typeof action === 'function') action()
    },
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    async createAPIKey() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/api-key`, {
          method: 'POST',
          body: JSON.stringify({
            ...this.createAPIKeyRequest,
          }),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.newKey = object.apiKeyToken
          this.keyCopied = false
          this.showFormModal = false
          this.showSuccessModal = true
        }
      } catch (error) {
        return
      }
    },

    async copyApiKey() {
      try {
        await navigator.clipboard.writeText(this.newKey)
        this.keyCopied = true
        toast.success('API key copied to clipboard')
        setTimeout(() => {
          this.keyCopied = false
        }, 2000)
      } catch (e) {
        toast.error('Could not copy automatically — select the key and copy it manually.')
      }
    },

    async activateKey(id) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/api-key/${id}/re-activate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getKeys()
        }
      } catch (error) {
        return
      }
    },

    async deactivate(id) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/api-key/${id}/deactivate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getKeys()
        }
      } catch (error) {
        return
      }
    },

    async deleteKey(id) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/api-key/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          this.getKeys()
        }
      } catch (error) {
        return
      }
    },

    async getKeys() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/api-key`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.apiKeys = object
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {},
  mounted() {
    document.title = 'Dashboard | API Keys'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
